import { computed, reactive, watch, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { DirectChatPeersResponse } from "@shopexperts/legacy-api";
import { getConvexAuthInfo } from "../convexClient";

type Peer = DirectChatPeersResponse["peers"][number];
type User = NonNullable<DirectChatPeersResponse["users"]>[number];

/**
 * Last identities fetched for this viewer, shared by the sidebar, inbox and header so a
 * conversation seen anywhere renders its name at once while the current read revalidates.
 */
const seen = reactive({ viewer: null as number | null, peers: new Map<string, Peer>(), users: new Map<number, User>() });

function remember(viewer: number | null, result: DirectChatPeersResponse) {
  if (seen.viewer !== viewer) {
    seen.viewer = viewer;
    seen.peers = new Map();
    seen.users = new Map();
  }
  result.peers.forEach(peer => seen.peers.set(peer.thread_id, peer));
  result.users?.forEach(user => seen.users.set(user.user_id, user));
}

/** Current display identities come from Laravel, never the room's historical snapshot. */
export function useDirectChatPeers(threadIds: Ref<string[]>) {
  const api = useApi();
  const ids = computed(() => [...new Set(threadIds.value)].sort());
  const viewer = computed(() => getConvexAuthInfo()?.orgId ?? null);
  const query = useQuery({
    queryKey: computed(() => ["direct-chat-peers", ids.value.length ? viewer.value : null, ids.value]),
    enabled: computed(() => ids.value.length > 0),
    queryFn: async () => {
      const requested = ids.value;
      const result = await api.directChatrooms.participantsDisplay({ query: { thread_ids: requested } });
      if (requested.some(id => !result.peers.some(peer => peer.thread_id === id))) throw new Error("Participant display is incomplete");
      return result;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  watch(query.data, result => { if (result) remember(viewer.value, result); }, { immediate: true });

  const current = computed(() => new Map(query.data.value?.peers.map(peer => [peer.thread_id, peer])));
  const known = (id: string) => (seen.viewer === viewer.value ? seen.peers.get(id) : undefined);
  const peer = (id: string) => current.value.get(id) ?? known(id);
  const name = (id: string) => peer(id)?.name ?? (query.error.value ? "Profile unavailable" : "Loading profile…");
  const users = computed(() => query.data.value?.users
    ?? (seen.viewer === viewer.value && seen.users.size ? [...seen.users.values()] : undefined));
  return { ...query, peer, name, users };
}
